// Optional clinical setup on step 3 of AddPatientInfo.
//
// The patient does not exist until Save, so the Case and Diagnosis/PMB panes
// capture drafts that AddPatientInfo posts right after the patient is
// created, with the same payload shapes as CaseModal.jsx (createCase) and
// Diagnosis.jsx (createPatientDiagnosis). The Referral pane is the
// pre-existing referred-by fields, passed in as children: those save on the
// patient document itself, exactly as before.
//
// The ICD-10 search is INLINE, not the shared ICD10PickerModal: this panel
// already lives inside the add-patient modal, and a modal stacked on a
// static-backdrop modal both looks wrong and left the outer modal's buttons
// unresponsive after the inner one closed.

import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Form,
  ListGroup,
  OverlayTrigger,
  Row,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import {
  searchICD10,
  ICD10_SEARCH_MIN_LENGTH,
  ICD10_SEARCH_MAX_RESULTS,
} from "../../afinanceapi";

export const icdCodeOf = (icd) => icd?.ICD10_Code || icd?.ICD10_3_Code || "";
const icdDescOf = (icd) =>
  icd?.ICD10_Description || icd?.ICD10_3_Description || "";

export const emptyCaseDraft = {
  description: "",
  refName: "",
  refBHF: "",
  refHPCSA: "",
  authorizationNumber: "",
  authorizedBy: "",
  startDate: "",
  endDate: "",
  maxSessions: "",
  icds: [], // [{ item, isPrimary }]
};

export const emptyDiagnosisDraft = {
  date: new Date().toISOString().slice(0, 10),
  note: "",
  icds: [], // [{ item, isPrimary, useOnInvoice, isPMB, pmbAuthNo, pmbStartDate, pmbEndDate }]
};

export const caseDraftHasContent = (d) =>
  !!(
    d.description ||
    d.refName ||
    d.authorizationNumber ||
    (d.icds && d.icds.length)
  );

export const diagnosisDraftHasContent = (d) =>
  !!(d.note || (d.icds && d.icds.length));

/** Error string when the drafts cannot be submitted as captured, else null. */
export const validateClinicalDrafts = (caseDraft, diagnosisDraft) => {
  if (caseDraftHasContent(caseDraft) && caseDraft.icds.length === 0) {
    return "The case needs at least one ICD-10 code (a case always carries a primary diagnosis). Add codes on the Create Case tab, or clear its fields.";
  }
  if (diagnosisDraftHasContent(diagnosisDraft)) {
    if (diagnosisDraft.icds.length === 0) {
      return "The diagnosis needs at least one ICD-10 code. Add codes on the Diagnosis / PMB tab, or clear its note.";
    }
    const missingAuth = diagnosisDraft.icds.find(
      (s) => s.isPMB && !(s.pmbAuthNo || "").trim()
    );
    if (missingAuth) {
      return `PMB code ${icdCodeOf(missingAuth.item)} needs an authorisation number.`;
    }
  }
  return null;
};

const MODES = [
  { key: "case", label: "Create Case" },
  { key: "diagnosis", label: "Diagnosis / PMB" },
  { key: "referral", label: "Referral" },
];

export default function ClinicalSetupPanel({
  mode,
  onModeChange,
  caseDraft,
  onCaseChange,
  diagnosisDraft,
  onDiagnosisChange,
  referralHasContent = false,
  children,
}) {
  const setCase = (patch) => onCaseChange({ ...caseDraft, ...patch });
  const setDiagnosis = (patch) =>
    onDiagnosisChange({ ...diagnosisDraft, ...patch });

  // Inline ICD-10 search, one instance shared by whichever pane is open.
  // Same API, minimum length and debounce as the shared ICD10PickerModal.
  const [icdQuery, setIcdQuery] = useState("");
  const [icdResults, setIcdResults] = useState([]);
  const [icdLoading, setIcdLoading] = useState(false);
  const tooShort = icdQuery.trim().length < ICD10_SEARCH_MIN_LENGTH;

  useEffect(() => {
    if (tooShort) {
      setIcdResults([]);
      setIcdLoading(false);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    setIcdLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await searchICD10(icdQuery.trim(), {
          signal: controller.signal,
        });
        if (!cancelled) setIcdResults(Array.isArray(res) ? res : []);
      } catch {
        // aborted: a newer search has taken over
      } finally {
        if (!cancelled) setIcdLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(t);
    };
  }, [icdQuery, tooShort]);

  // Clear the search when switching panes so results never land in the
  // wrong draft.
  useEffect(() => {
    setIcdQuery("");
    setIcdResults([]);
  }, [mode]);

  const draftOf = (listKey) => (listKey === "case" ? caseDraft : diagnosisDraft);
  const setIcds = (listKey, icds) =>
    listKey === "case" ? setCase({ icds }) : setDiagnosis({ icds });

  const addIcd = (listKey, item) => {
    const draft = draftOf(listKey);
    const key = icdCodeOf(item);
    if (!key || draft.icds.some((s) => icdCodeOf(s.item) === key)) return;
    // Mirrors the shared picker: the first code becomes primary only when
    // MedPrax allows it as one.
    const isFirst = draft.icds.length === 0;
    const entry = {
      item,
      isPrimary: isFirst && item?.ValidPrimary === "Y",
      ...(listKey === "diagnosis"
        ? {
            useOnInvoice: true,
            isPMB: false,
            pmbAuthNo: "",
            pmbStartDate: "",
            pmbEndDate: "",
          }
        : {}),
    };
    setIcds(listKey, [...draft.icds, entry]);
    setIcdQuery("");
    setIcdResults([]);
  };

  const setPrimary = (listKey, index) => {
    setIcds(
      listKey,
      draftOf(listKey).icds.map((s, i) => ({ ...s, isPrimary: i === index }))
    );
  };

  const removeIcd = (listKey, index) => {
    setIcds(listKey, draftOf(listKey).icds.filter((_, i) => i !== index));
  };

  const hasContent = {
    case: caseDraftHasContent(caseDraft),
    diagnosis: diagnosisDraftHasContent(diagnosisDraft),
    referral: referralHasContent,
  };

  const icdSearch = (listKey) => {
    const selectedKeys = new Set(
      draftOf(listKey).icds.map((s) => icdCodeOf(s.item))
    );
    return (
      <div className="mb-2">
        <Form.Group>
          <Form.Label>ICD-10 codes</Form.Label>
          <Form.Control
            type="search"
            size="sm"
            placeholder="Search by description or code (e.g. M54.5)"
            value={icdQuery}
            onChange={(e) => setIcdQuery(e.target.value)}
          />
        </Form.Group>
        {icdQuery.trim() && tooShort && (
          <small className="text-muted">
            Type at least {ICD10_SEARCH_MIN_LENGTH} characters to search.
          </small>
        )}
        {icdLoading && (
          <div className="d-flex align-items-center gap-2 small mt-1">
            <Spinner animation="border" size="sm" /> Searching…
          </div>
        )}
        {icdResults.length > 0 && (
          <ListGroup
            className="mt-1"
            style={{ maxHeight: 220, overflowY: "auto" }}
          >
            {icdResults.map((item, idx) => {
              const key = icdCodeOf(item);
              const already = selectedKeys.has(key);
              return (
                <ListGroup.Item
                  action
                  key={key || `row-${idx}`}
                  disabled={already || !key}
                  onClick={() => addIcd(listKey, item)}
                  className="py-1 small"
                >
                  <Badge bg={already ? "secondary" : "primary"} className="me-2">
                    {already ? "Added" : key || "No code"}
                  </Badge>
                  {icdDescOf(item)}
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        )}
        {!tooShort && icdResults.length >= ICD10_SEARCH_MAX_RESULTS && (
          <small className="text-muted">
            Showing the first {ICD10_SEARCH_MAX_RESULTS} matches. Refine the
            search to narrow the list.
          </small>
        )}
      </div>
    );
  };

  const icdRow = (listKey, s, i, extras = null) => {
    const primaryBlocked = s.item?.ValidPrimary === "N";
    return (
      <div
        key={`${icdCodeOf(s.item)}-${i}`}
        className="clinical-icd-row d-flex align-items-center flex-wrap gap-2 mb-1 small"
      >
        <Form.Check
          type="radio"
          name={`${listKey}-primary-icd`}
          id={`${listKey}-primary-${i}`}
          label={
            primaryBlocked ? (
              <span className="text-muted">Not allowed as primary</span>
            ) : (
              "Primary"
            )
          }
          checked={!!s.isPrimary}
          disabled={primaryBlocked}
          onChange={() => setPrimary(listKey, i)}
        />
        <Badge bg="secondary">{icdCodeOf(s.item)}</Badge>
        <span className="text-muted text-truncate" style={{ maxWidth: 260 }}>
          {icdDescOf(s.item)}
        </span>
        {extras}
        <OverlayTrigger placement="top" overlay={<Tooltip>Remove code</Tooltip>}>
          <Button
            variant="link"
            className="text-secondary text-decoration-none p-0"
            style={{ width: 24, height: 24 }}
            aria-label={`Remove ${icdCodeOf(s.item)}`}
            onClick={() => removeIcd(listKey, i)}
          >
            <i className="fa-solid fa-xmark" />
          </Button>
        </OverlayTrigger>
      </div>
    );
  };

  return (
    <>
      <h5 className="text-primary">Clinical Setup</h5>
      <Row className="mt-1 mb-2">
        <Form.Text>
          Optional. A case or diagnosis captured here is created for the
          patient when you save. Skip this if it is not needed yet.
        </Form.Text>
      </Row>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {MODES.map((m) => (
          <Button
            key={m.key}
            size="sm"
            style={{ minWidth: 140 }}
            variant={mode === m.key ? "primary" : "outline-primary"}
            onClick={() => onModeChange(mode === m.key ? "" : m.key)}
          >
            {m.label}
            {hasContent[m.key] && <i className="fa-solid fa-check ms-2" />}
          </Button>
        ))}
      </div>

      {mode === "case" && (
        <>
          <Row className="g-2 mb-2">
            <Col md={8}>
              <Form.Group>
                <Form.Label>Case description</Form.Label>
                <Form.Control
                  value={caseDraft.description}
                  onChange={(e) => setCase({ description: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Max sessions</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={caseDraft.maxSessions}
                  onChange={(e) => setCase({ maxSessions: e.target.value })}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="g-2 mb-2">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Referring doctor</Form.Label>
                <Form.Control
                  value={caseDraft.refName}
                  onChange={(e) => setCase({ refName: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>BHF number</Form.Label>
                <Form.Control
                  value={caseDraft.refBHF}
                  onChange={(e) => setCase({ refBHF: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>HPCSA number</Form.Label>
                <Form.Control
                  value={caseDraft.refHPCSA}
                  onChange={(e) => setCase({ refHPCSA: e.target.value })}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="g-2 mb-2">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Authorisation number</Form.Label>
                <Form.Control
                  value={caseDraft.authorizationNumber}
                  onChange={(e) =>
                    setCase({ authorizationNumber: e.target.value })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Authorised by</Form.Label>
                <Form.Control
                  value={caseDraft.authorizedBy}
                  onChange={(e) => setCase({ authorizedBy: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Start date</Form.Label>
                <Form.Control
                  type="date"
                  value={caseDraft.startDate}
                  onChange={(e) => setCase({ startDate: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>End date</Form.Label>
                <Form.Control
                  type="date"
                  value={caseDraft.endDate}
                  onChange={(e) => setCase({ endDate: e.target.value })}
                />
              </Form.Group>
            </Col>
          </Row>
          {icdSearch("case")}
          {caseDraft.icds.map((s, i) => icdRow("case", s, i))}
        </>
      )}

      {mode === "diagnosis" && (
        <>
          <Row className="g-2 mb-2">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Diagnosis date</Form.Label>
                <Form.Control
                  type="date"
                  value={diagnosisDraft.date}
                  onChange={(e) => setDiagnosis({ date: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={9}>
              <Form.Group>
                <Form.Label>Note</Form.Label>
                <Form.Control
                  value={diagnosisDraft.note}
                  onChange={(e) => setDiagnosis({ note: e.target.value })}
                />
              </Form.Group>
            </Col>
          </Row>
          {icdSearch("diagnosis")}
          {diagnosisDraft.icds.map((s, i) =>
            icdRow(
              "diagnosis",
              s,
              i,
              <>
                <Form.Check
                  type="switch"
                  id={`diag-pmb-${i}`}
                  label="PMB"
                  checked={!!s.isPMB}
                  onChange={(e) => {
                    const icds = diagnosisDraft.icds.map((d, di) =>
                      di === i ? { ...d, isPMB: e.target.checked } : d
                    );
                    setDiagnosis({ icds });
                  }}
                />
                {s.isPMB && (
                  <>
                    <Form.Control
                      size="sm"
                      style={{ width: 150 }}
                      placeholder="PMB auth number"
                      value={s.pmbAuthNo}
                      onChange={(e) => {
                        const icds = diagnosisDraft.icds.map((d, di) =>
                          di === i ? { ...d, pmbAuthNo: e.target.value } : d
                        );
                        setDiagnosis({ icds });
                      }}
                    />
                    <Form.Control
                      size="sm"
                      type="date"
                      style={{ width: 150 }}
                      aria-label="PMB start date"
                      value={s.pmbStartDate}
                      onChange={(e) => {
                        const icds = diagnosisDraft.icds.map((d, di) =>
                          di === i ? { ...d, pmbStartDate: e.target.value } : d
                        );
                        setDiagnosis({ icds });
                      }}
                    />
                    <Form.Control
                      size="sm"
                      type="date"
                      style={{ width: 150 }}
                      aria-label="PMB end date"
                      value={s.pmbEndDate}
                      onChange={(e) => {
                        const icds = diagnosisDraft.icds.map((d, di) =>
                          di === i ? { ...d, pmbEndDate: e.target.value } : d
                        );
                        setDiagnosis({ icds });
                      }}
                    />
                  </>
                )}
              </>
            )
          )}
        </>
      )}

      {mode === "referral" && children}
    </>
  );
}
